import React, { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ConfirmDialog } from "@/components/ui/ConfirmDialog";
import { PanelHeaderAction } from "@/components/ui/PanelHeaderAction";
import { CloseIcon, GoogleIcon, LinkIcon } from "@/components/icons";
import { WorkspacePanel } from "@/domains/Ideas/components/WorkspacePanel";
import {
  agencySettingsService,
  GmailStatusResponse,
} from "./agencySettings.service";

/**
 * Per-agency Gmail mailbox connection panel (DD-047).
 *
 * Reads the connection status. Connect, a header action, redirects the
 * browser to Google's consent screen; Disconnect replaces it while a
 * mailbox is connected. The OAuth callback redirects back to /setup with
 * `?gmail=connected` or `?gmail=error&reason=...`; that state is read once
 * on mount and translated into a banner.
 */
export function GmailConnectCard() {
  const [collapsed, setCollapsed] = useState(false);
  const [status, setStatus] = useState<GmailStatusResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [confirmDisconnect, setConfirmDisconnect] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [banner, setBanner] = useState<
    { kind: "success" | "error"; text: string } | null
  >(null);

  const [searchParams, setSearchParams] = useSearchParams();

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const s = await agencySettingsService.getGmailStatus();
      setStatus(s);
    } catch (e) {
      setBanner({
        kind: "error",
        text: e instanceof Error ? e.message : "Failed to load status",
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const gmailParam = searchParams.get("gmail");
    if (gmailParam === "connected") {
      setBanner({ kind: "success", text: "Gmail connected successfully." });
      const next = new URLSearchParams(searchParams);
      next.delete("gmail");
      next.delete("reason");
      setSearchParams(next, { replace: true });
    } else if (gmailParam === "error") {
      const reason = searchParams.get("reason") || "Unknown error";
      setBanner({ kind: "error", text: `Gmail connection failed: ${reason}` });
      const next = new URLSearchParams(searchParams);
      next.delete("gmail");
      next.delete("reason");
      setSearchParams(next, { replace: true });
    }
    refresh();
  }, [refresh, searchParams, setSearchParams]);

  const handleConnect = useCallback(async () => {
    setConnecting(true);
    try {
      const authUrl = await agencySettingsService.startGmailConnect();
      window.location.href = authUrl;
    } catch (e) {
      setBanner({
        kind: "error",
        text: e instanceof Error ? e.message : "Failed to start OAuth",
      });
      setConnecting(false);
    }
  }, []);

  const handleDisconnect = useCallback(async () => {
    setConfirmDisconnect(false);
    setDisconnecting(true);
    try {
      await agencySettingsService.disconnectGmail();
      setBanner({ kind: "success", text: "Gmail disconnected." });
      await refresh();
    } catch (e) {
      setBanner({
        kind: "error",
        text: e instanceof Error ? e.message : "Failed to disconnect",
      });
    } finally {
      setDisconnecting(false);
    }
  }, [refresh]);

  const credential = status?.connected ? status.credential : null;
  const oauthMisconfigured = status !== null && !status.oauthConfigured;

  const action = loading || oauthMisconfigured ? null : credential ? (
    <PanelHeaderAction
      variant="secondary"
      label="Disconnect"
      icon={<CloseIcon className="w-4 h-4" />}
      onClick={() => setConfirmDisconnect(true)}
      disabled={disconnecting}
    />
  ) : (
    <PanelHeaderAction
      label="Connect Gmail"
      icon={<LinkIcon className="w-4 h-4" />}
      onClick={handleConnect}
      disabled={connecting}
    />
  );

  return (
    <>
      <WorkspacePanel
        title="Gmail (outreach)"
        icon={<GoogleIcon className="w-4 h-4" />}
        actions={action}
        collapsed={collapsed}
        onToggleCollapse={() => setCollapsed((c) => !c)}
        className={collapsed ? "" : "h-full"}
        loading={loading}
      >
        <div className="space-y-4">
          <p className="text-sm text-gray-500">
            Send cold outreach from your own Workspace mailbox via the Gmail
            API. Lands in Primary far more often than ESP-routed sends.
            Magic-link emails stay on Resend.
          </p>

          {banner && (
            <div
              className={
                "px-3 py-2 rounded-lg text-sm " +
                (banner.kind === "success"
                  ? "bg-green-50 text-green-800"
                  : "bg-red-50 text-red-800")
              }
            >
              {banner.text}
            </div>
          )}

          {oauthMisconfigured ? (
            <div className="text-sm text-amber-800 bg-amber-50 rounded-lg px-3 py-2">
              Gmail OAuth is not configured on the server. Set
              {" "}<code className="font-mono">GMAIL_CLIENT_ID</code>,
              {" "}<code className="font-mono">GMAIL_CLIENT_SECRET</code>,
              {" "}<code className="font-mono">GMAIL_REDIRECT_URI</code>, and
              {" "}<code className="font-mono">SECRETS_ENCRYPTION_KEY</code>.
            </div>
          ) : credential ? (
            <div className="text-sm">
              <div className="font-medium text-gray-900">
                {credential.emailAddress}
              </div>
              {credential.displayName && (
                <div className="text-gray-500">{credential.displayName}</div>
              )}
              <div className="text-xs text-gray-400 mt-1">
                Connected{" "}
                {credential.connectedAt
                  ? new Date(credential.connectedAt).toLocaleString()
                  : "—"}
                {credential.lastUsedAt && (
                  <> · Last used {new Date(credential.lastUsedAt).toLocaleString()}</>
                )}
              </div>
            </div>
          ) : (
            <p className="text-sm text-gray-600">
              No mailbox connected yet. Outreach will fall back to Resend.
            </p>
          )}
        </div>
      </WorkspacePanel>

      <ConfirmDialog
        isOpen={confirmDisconnect}
        onClose={() => setConfirmDisconnect(false)}
        onConfirm={handleDisconnect}
        title="Disconnect Gmail"
        message="Outreach will fall back to Resend until you reconnect. Existing sent-email history is preserved. Continue?"
        confirmText="Disconnect"
        variant="warning"
      />
    </>
  );
}
